// ConfirmBar — the one press that ends a review lane, and the sentence that says
// why it is grayed out.
//
// Four lanes end the same way: read a document, clear what the amber says, press
// once. Scan pins its bar to the bottom of the viewport because the reader is
// holding a box; quote, catalog import and packing list sit in the flow. Each
// had its own copy of the arrangement, which is why the four drifted.
//
// This owns the ARRANGEMENT only. Every string stays with its lane: the button
// copy, the busy copy, the blocker sentences. So does `disabled` — a bar that
// inferred it from `blockers.length` would be wrong on the scan lane, whose gate
// also wants two checkboxes ticked, and one that owned the copy would flatten
// four vocabularies into one.

import React from 'react';
import { DOCK_MOTION } from '../ChatBot/flerpieDock';
import { buttonClass } from '../ui/Button';

export interface ConfirmBarProps {
  /**
   * Where the bar sits. `fixed` pins it over the bottom of the viewport and the
   * kit owns the backdrop; `sticky` is that same skin riding the scroll; `static`
   * is in-flow and wears whatever `className` gives it.
   *
   * PADDING CONTRACT, `fixed` and `sticky`: the bar leaves the page's flow, so it
   * covers whatever ends up underneath. The PAGE keeps the bottom padding that
   * makes room for it (the scan lane's `pb-44 sm:pb-28`). It cannot move in here,
   * because the bar's height depends on the `aside` the lane passes.
   */
  mode: 'fixed' | 'sticky' | 'static';
  /**
   * Appended to the outer container. The in-flow lanes pass their whole section
   * skin through here, spacing included, because `static` adds no skin of its own.
   */
  className?: string;
  /**
   * The reasons the press is refused. As a list, one line each. As prose, the lane
   * passes AT MOST ONE string and it is the whole sentence, template and all.
   */
  blockers: string[];
  presentation?: 'list' | 'prose';
  /** Lands on the UL, not the container, so a lane's existing query still finds it. */
  blockersTestId?: string;
  /**
   * Opt-in `role="status"` on the blockers region. Opt-in and not automatic: two
   * of these pages already carry another live region, and quietly giving three
   * more bars a second one would change what a screen reader announces without
   * anybody deciding to.
   */
  statusRole?: boolean;
  /** Primary button copy, computed by the lane. */
  label: React.ReactNode;
  /** Shown in its place while `busy`. */
  busyLabel: React.ReactNode;
  busy: boolean;
  /** Fully lane-computed. The bar never infers it from the blockers. */
  disabled: boolean;
  onConfirm: () => void;
  /**
   * The left region of a `fixed` or `sticky` bar — scan's affirmation checkboxes.
   * `static` has no left region and does not render it.
   */
  aside?: React.ReactNode;
  /** Between the blockers and the button: the catalog and packing explainers. */
  beforeButton?: React.ReactNode;
  /** Below the button: the quote lane's explainer. */
  afterButton?: React.ReactNode;
  /** A whole second action, passed as JSX. Catalog and packing send Start over. */
  secondaryButton?: React.ReactNode;
}

export const ConfirmBar: React.FC<ConfirmBarProps> = ({
  mode,
  className,
  blockers,
  presentation = 'list',
  blockersTestId,
  statusRole,
  label,
  busyLabel,
  busy,
  disabled,
  onConfirm,
  aside,
  beforeButton,
  afterButton,
  secondaryButton,
}) => {
  const role = statusRole ? 'status' : undefined;

  // Nothing renders when nothing blocks. An empty UL would still be a list to
  // every query that counts them, and all four lanes count on it being absent.
  const blockersRegion =
    blockers.length === 0 ? null : presentation === 'prose' ? (
      <span className="text-[11px] text-on-surface-variant" role={role}>
        {blockers[0]}
      </span>
    ) : (
      <ul className="space-y-1" data-testid={blockersTestId} role={role}>
        {blockers.map((b) => (
          <li key={b} className="text-xs text-amber-300/90 leading-snug">
            {b}
          </li>
        ))}
      </ul>
    );

  // ONE axis sets the button's width and height: whether a second action stands
  // beside it. A pair reads as a right-aligned row at its natural size; a lone
  // confirm fills the phone's width and takes the taller thumb target, because
  // it is the only thing on that row worth pressing.
  const primary = (
    <button
      type="button"
      onClick={onConfirm}
      disabled={disabled}
      className={secondaryButton ? buttonClass('filled', 'md') : `${buttonClass('filled', 'lg')} w-full sm:w-auto`}
    >
      {busy ? busyLabel : label}
    </button>
  );

  const buttons = secondaryButton ? (
    <div className="flex justify-end gap-2">
      {secondaryButton}
      {primary}
    </div>
  ) : (
    primary
  );

  // A bar in the flow is a section of the page; a bar pinned over the viewport is
  // chrome. So `static` renders the <section> the three in-flow lanes already
  // had, and wraps nothing around it.
  if (mode === 'static') {
    return (
      <section className={className}>
        {blockersRegion}
        {beforeButton}
        {buttons}
        {afterButton}
      </section>
    );
  }

  const skin =
    mode === 'fixed'
      ? `fixed bottom-0 left-[var(--flerpie-dock,0px)] right-0 transition-[left] ${DOCK_MOTION} bg-surface border-t border-gray-800 px-4 py-3`
      : 'sticky bottom-0 bg-surface border-t border-gray-800 px-4 py-3';

  return (
    <div className={className ? `${skin} ${className}` : skin}>
      <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {aside}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          {blockersRegion}
          {buttons}
        </div>
      </div>
    </div>
  );
};

export default ConfirmBar;
